import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface MonthlyPassengerData {
  year: number;
  month: number;
  period: number;
  totalPassengers: number;
  date: string;
}

interface MonthlyPassengerChartProps {
  data: MonthlyPassengerData[];
  startYear: number | null;
  startMonth: number | null;
  endYear: number | null;
  endMonth: number | null;
  loading?: boolean;
  title?: string;
}

export const MonthlyPassengerChart: React.FC<MonthlyPassengerChartProps> = ({
  data,
  startYear,
  startMonth,
  endYear,
  endMonth,
  loading = false,
  title = 'Last 12 Months Arriving Passengers by Month',
}) => {
  const theme = useTheme();

  // Color for the line
  const lineColor = '#0050ad'; // Matching the ADR color from color scheme

  // Custom tooltip
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const monthData = payload[0].payload;
      const date = parseISO(monthData.date + '-01');
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {format(date, 'MMMM yyyy')}
          </Typography>
          <Typography variant="body2" color="primary">
            Passengers: {payload[0].value.toLocaleString()}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Skeleton variant="text" width="60%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={350} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.length === 0) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            {title}
          </Typography>
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="body2" color="text.secondary">
              No monthly passenger data available for this market
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
          {title}
        </Typography>

        <Box sx={{ width: '100%', height: 350 }}>
          <ResponsiveContainer>
            <LineChart
              data={data}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                angle={-45}
                textAnchor="end"
                height={80}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  const date = parseISO(value + '-01');
                  return format(date, 'MMM yyyy');
                }}
              />
              <YAxis
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  if (value >= 1000000) {
                    return `${(value / 1000000).toFixed(1)}M`;
                  } else if (value >= 1000) {
                    return `${(value / 1000).toFixed(0)}K`;
                  }
                  return value.toString();
                }}
                label={{
                  value: 'Arriving Passengers',
                  angle: -90,
                  position: 'insideLeft',
                  style: { fontSize: 12 },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="totalPassengers"
                stroke={lineColor}
                strokeWidth={2}
                dot={{ fill: lineColor, r: 4 }}
                activeDot={{ r: 6 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          Total arriving passengers aggregated from all nearby airports by month
        </Typography>
        {startYear && startMonth && endYear && endMonth && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Period: {new Date(startYear, startMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })} - {new Date(endYear, endMonth - 1).toLocaleDateString('en-US', {
              month: 'short',
              year: 'numeric'
            })}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};
